import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { workspaceService, teamService } from '../lib/services';
import type { Workspace, Team, MembershipRole } from '../types/api';
import { useAuth } from './AuthContext';

interface WorkspaceContextType {
    // Current state
    workspaces: Workspace[];
    currentWorkspace: Workspace | null;
    teams: Team[];
    isLoading: boolean;
    error: string | null;

    // Derived state
    /** The current user's role in the active workspace (null if not a member) */
    userRole: MembershipRole | null;
    /** Whether the current user is an owner in the active workspace */
    isOwner: boolean;
    /** Whether the current user can hire replicas (has employer or owner role) */
    canHire: boolean;

    // Actions
    setCurrentWorkspace: (workspace: Workspace | null) => void;
    refreshWorkspaces: () => Promise<void>;
    createWorkspace: (name: string, options?: {
        visibility?: 'private' | 'invite_only';
        company?: { name: string; vatId?: string; country?: string };
    }) => Promise<Workspace | null>;
    joinWorkspace: (inviteCode: string) => Promise<Workspace | null>;
    refreshTeams: () => Promise<void>;
    createTeam: (name: string, description?: string) => Promise<Team | null>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
    const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
    const [teams, setTeams] = useState<Team[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Derive user's role in the current workspace
    const userRole = useMemo<MembershipRole | null>(() => {
        if (!currentWorkspace?.memberships || !user) return null;
        const membership = currentWorkspace.memberships.find(m => m.userId === user.uid);
        return membership?.role ?? null;
    }, [currentWorkspace, user]);

    const isOwner = userRole === 'owner';
    const canHire = userRole === 'owner' || userRole === 'employer';

    // Fetch workspaces when user changes
    const refreshWorkspaces = useCallback(async () => {
        if (!user) {
            setWorkspaces([]);
            setCurrentWorkspace(null);
            return;
        }

        setIsLoading(true);
        setError(null);

        const response = await workspaceService.list();

        if (response.error) {
            setError(response.error);
        } else if (response.data) {
            setWorkspaces(response.data.workspaces);
            // Auto-select first workspace if none selected
            if (!currentWorkspace && response.data.workspaces.length > 0) {
                setCurrentWorkspace(response.data.workspaces[0]);
            }
        }

        setIsLoading(false);
    }, [user, currentWorkspace]);

    // Fetch teams for current workspace
    const refreshTeams = useCallback(async () => {
        if (!currentWorkspace) {
            setTeams([]);
            return;
        }

        const response = await teamService.list(currentWorkspace.id);

        if (response.data) {
            setTeams(response.data.teams);
        }
    }, [currentWorkspace]);

    // Create workspace (neutral container — roles define semantics)
    const createWorkspace = useCallback(async (
        name: string,
        options?: {
            visibility?: 'private' | 'invite_only';
            company?: { name: string; vatId?: string; country?: string };
        }
    ): Promise<Workspace | null> => {
        const response = await workspaceService.create({
            name,
            visibility: options?.visibility,
            company: options?.company,
        });

        if (response.data) {
            await refreshWorkspaces();
            return response.data.workspace;
        }

        setError(response.error || 'Failed to create workspace');
        return null;
    }, [refreshWorkspaces]);

    // Join a workspace via invitation code (Phase 1 placeholder)
    const joinWorkspace = useCallback(async (
        _inviteCode: string
    ): Promise<Workspace | null> => {
        // TODO: Implement invitation acceptance endpoint
        // For now, this is a placeholder for the invitation flow
        setError('Invitation system coming soon');
        return null;
    }, []);

    // Create team
    const createTeam = useCallback(async (
        name: string,
        description?: string
    ): Promise<Team | null> => {
        if (!currentWorkspace) {
            setError('No workspace selected');
            return null;
        }

        const response = await teamService.create({
            workspaceId: currentWorkspace.id,
            name,
            description,
        });

        if (response.data) {
            await refreshTeams();
            return response.data.team;
        }

        setError(response.error || 'Failed to create team');
        return null;
    }, [currentWorkspace, refreshTeams]);

    // Load workspaces on user change
    useEffect(() => {
        refreshWorkspaces();
    }, [user]);

    // Load teams when workspace changes
    useEffect(() => {
        refreshTeams();
    }, [currentWorkspace]);

    return (
        <WorkspaceContext.Provider
            value={{
                workspaces,
                currentWorkspace,
                teams,
                isLoading,
                error,
                userRole,
                isOwner,
                canHire,
                setCurrentWorkspace,
                refreshWorkspaces,
                createWorkspace,
                joinWorkspace,
                refreshTeams,
                createTeam,
            }}
        >
            {children}
        </WorkspaceContext.Provider>
    );
}

export function useWorkspace() {
    const context = useContext(WorkspaceContext);
    if (context === undefined) {
        throw new Error('useWorkspace must be used within a WorkspaceProvider');
    }
    return context;
}
